import { Check, Clock } from "lucide-react";
import type { MsaContract, MsaPrefill } from "@workspace/api-client-react";

// On-screen HTML rendering of the Independent Field Sales Agent Agreement (the
// "MSA"). The body text comes from the server (MsaStatus.contract) so this stays
// identical to the executed PDF — there is one source of truth for the legal
// text. Shared across the onboarding wizard (sign step), the post-submit status
// screen, and the account-manager counter-sign review, so all parties read the
// SAME document. Presentational only: signing/counter-signing actions live with
// the caller.

function formatSignDate(epochMs: number | null | undefined): string {
  if (!epochMs) return "";
  return new Date(epochMs).toLocaleString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Africa/Johannesburg",
  });
}

function ParticularRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right break-words">
        {value && value.trim().length > 0 ? value : "\u2014"}
      </span>
    </div>
  );
}

function SignatureBlock({
  roleLabel,
  name,
  at,
  image,
  ip,
  pendingLabel,
}: {
  roleLabel: string;
  name: string | null | undefined;
  at: number | null | undefined;
  image?: string | null;
  ip?: string | null;
  pendingLabel: string;
}) {
  const signed = !!(name && name.trim().length > 0);
  const signedOn = formatSignDate(at);
  return (
    <div className="flex-1 rounded-xl border border-gray-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {roleLabel}
      </p>
      {signed ? (
        <div className="mt-2">
          {image ? (
            <img
              src={image}
              alt={`Signature of ${name}`}
              className="h-16 w-auto max-w-full object-contain"
            />
          ) : null}
          <p className="mt-1 text-sm font-semibold text-[#0B132B]">{name}</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-[#00A859]">
            <Check className="w-3.5 h-3.5" />
            Signed{signedOn ? ` ${signedOn}` : ""}
          </p>
          {ip ? (
            <p className="mt-0.5 text-[11px] text-gray-400">IP {ip}</p>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-amber-600">
          <Clock className="w-3.5 h-3.5" /> {pendingLabel}
        </p>
      )}
    </div>
  );
}

export function MsaContractDocument({
  contract,
  prefill,
  agentSignedName,
  agentSignedAt,
  agentSignatureImage,
  agentSignedIp,
  adminSignedName,
  adminSignedAt,
  adminSignatureImage,
  adminSignedIp,
  className,
}: {
  contract: MsaContract;
  prefill: MsaPrefill;
  agentSignedName?: string | null;
  agentSignedAt?: number | null;
  agentSignatureImage?: string | null;
  agentSignedIp?: string | null;
  adminSignedName?: string | null;
  adminSignedAt?: number | null;
  adminSignatureImage?: string | null;
  adminSignedIp?: string | null;
  className?: string;
}) {
  const addressParts = [
    prefill.addressLine,
    prefill.city,
    prefill.province,
    prefill.postalCode,
  ].filter((p) => p && p.trim().length > 0);
  const address = addressParts.length > 0 ? addressParts.join(", ") : null;

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white ${className ?? ""}`}
    >
      <div className="p-4 sm:p-6 space-y-5">
        <header className="space-y-2 border-b border-gray-100 pb-4">
          <h2 className="text-lg font-bold text-[#0B132B]">{contract.title}</h2>
          <p className="text-sm leading-relaxed text-gray-600">
            {contract.intro}
          </p>
        </header>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-[#0B132B]">
            Agent particulars
          </h3>
          <div className="rounded-xl border border-gray-100 bg-gray-50 divide-y divide-gray-100">
            <ParticularRow label="Full name" value={prefill.fullName} />
            <ParticularRow label="ID number" value={prefill.idNumber} />
            <ParticularRow label="Phone" value={prefill.phone} />
            <ParticularRow label="Email" value={prefill.email} />
            <ParticularRow
              label="Operating area"
              value={prefill.preferredOperatingArea}
            />
            {address ? (
              <ParticularRow label="Address" value={address} />
            ) : null}
          </div>
        </section>

        <section className="space-y-4">
          {contract.clauses.map((clause) => (
            <div key={clause.heading} className="space-y-1">
              <h3 className="text-sm font-semibold text-[#0B132B]">
                {clause.heading}
              </h3>
              <p className="text-sm leading-relaxed text-gray-600">
                {clause.body}
              </p>
            </div>
          ))}
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-4">
          <p className="text-xs leading-relaxed text-gray-500">
            {contract.signatureNote}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <SignatureBlock
              roleLabel={contract.agentRoleLabel}
              name={agentSignedName}
              at={agentSignedAt}
              image={agentSignatureImage}
              ip={agentSignedIp}
              pendingLabel="Awaiting signature"
            />
            <SignatureBlock
              roleLabel={contract.mooyaRoleLabel}
              name={adminSignedName}
              at={adminSignedAt}
              image={adminSignatureImage}
              ip={adminSignedIp}
              pendingLabel="Awaiting counter-signature"
            />
          </div>
        </section>
      </div>
    </div>
  );
}
